// web/src/admin/AdminUsers.tsx (/admin/users) — by-user table: email, business, tier, status, last login (D15 §5).
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { AdminUserRow } from '../lib/types';
import { age } from '../lib/status';
import { PageHeader } from '../components/PageHeader';
import { AsyncBoundary } from '../components/AsyncBoundary';
import { Skeleton } from '../components/Skeleton';
import { DataTable } from '../components/DataTable';

const lastLogin = (s: number | null) => (s ? `${age(s)} ago` : 'never');

export function AdminUsers() {
  const q = useQuery({ queryKey: ['admin', 'users'], queryFn: () => api.adminUsers() });
  return (
    <>
      <PageHeader title="Users" subtitle="Who logged in, when, and which business." />
      <AsyncBoundary query={q} skeleton={<Skeleton variant="row" count={5} />}
        isEmpty={(r) => r.length === 0}
        empty={{ icon: 'user', title: 'No users yet' }}>
        {(rows) => (
          <DataTable<AdminUserRow>
            rows={rows} rowKey={(r) => r.email ?? r.business}
            columns={[
              { key: 'email', header: 'Email', render: (r) => <span className="text-ink">{r.email ?? '—'}</span> },
              { key: 'business', header: 'Business', render: (r) => r.business },
              { key: 'tier', header: 'Tier', render: (r) => r.tier },
              { key: 'status', header: 'Status', render: (r) => r.status },
              { key: 'lastLogin', header: 'Last login', render: (r) => lastLogin(r.lastLogin) },
            ]}
            mobileCard={(r) => (
              <div className="rounded-lg border border-hair bg-surface p-4">
                <div className="truncate font-medium text-ink">{r.email ?? '—'}</div>
                <div className="mt-1 flex items-center gap-3 text-xs text-muted">
                  <span>{r.business}</span><span>{r.tier}</span><span>{r.status}</span><span>{lastLogin(r.lastLogin)}</span>
                </div>
              </div>
            )} />
        )}
      </AsyncBoundary>
    </>
  );
}
